import type { SegmentDefinition } from '@masters-club/validation';

/**
 * Изображение анонса из API.
 */
export interface AnnouncementImage {
  id: number;
  announcementId: number;
  imageId: number;
  imageUrl?: string;
  aspectRatio: string; // '16:9' | '4:3' | '9:16'
  languageCode: string | null;
  type: 'background';
  order: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Image input для create/edit.
 */
export interface AnnouncementImageInput {
  imageId: number;
  imageUrl?: string;
  aspectRatio: string;
  languageCode: string | null;
  type: 'background';
  order: number;
}

/**
 * Анонс из API. Счётчики совпадают с колонками БД:
 * viewsCount (показы), clicksCount (переходы), closesCount (закрытия).
 */
export interface Announcement {
  id: number;
  linkUrl: string | null;
  audienceSegment: SegmentDefinition | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  viewsCount: number;
  clicksCount: number;
  closesCount: number;
  uniqueViewsCount: number;
  uniqueClicksCount: number;
  createdAt: string;
  updatedAt: string;
  images: AnnouncementImage[];
}

/**
 * Данные формы для create/update.
 */
export interface AnnouncementFormData {
  images: AnnouncementImageInput[];
  linkUrl: string | null;
  audienceSegment: SegmentDefinition | null;
  order: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

/**
 * Слоты изображений под девайсы. Три отдельных картинки гарантируют, что
 * контент не перекрывается на разных экранах (брейкпоинты платформы):
 *  mobile (<640px)  → 9:16
 *  tablet (≥640px)  → 4:3
 *  desktop (≥1024px)→ 16:9
 */
export const DEVICE_SLOTS = [
  { aspectRatio: '16:9', label: 'Desktop', description: '1920×1080' },
  { aspectRatio: '4:3', label: 'Tablet', description: '1536×1152' },
  { aspectRatio: '9:16', label: 'Mobile', description: '1080×1920' },
] as const;

/**
 * Тип события аналитики анонса.
 */
export type AnnouncementEventType = 'impression' | 'click' | 'close';

/**
 * Типы цели ссылки анонса. Внутренние — собираются в `/<resource>/<id>` и
 * открываются внутри платформы через openSmartLink; 'custom' — произвольный URL.
 */
export const LINK_TARGET_OPTIONS = [
  { value: 'custom', label: 'Своя ссылка' },
  { value: 'courses', label: 'Курс' },
  { value: 'subscription-plans', label: 'Абонемент' },
  { value: 'channel-passes', label: 'Канал-пасс' },
  { value: 'posts', label: 'Публикация' },
  { value: 'teachers', label: 'Преподаватель' },
] as const;

export type LinkTargetType = typeof LINK_TARGET_OPTIONS[number]['value'];

/**
 * URL первого фонового изображения анонса.
 */
export function getAnnouncementBackgroundUrl(announcement: Announcement): string | null {
  const images = announcement.images ?? [];
  const bgImage = images.find((img) => img.type === 'background');
  return bgImage?.imageUrl ?? images[0]?.imageUrl ?? null;
}

/**
 * Engagement rate (например CTR = clicks/views) в процентах.
 */
export function calculateEngagementRate(total: number, events: number): number {
  if (total === 0) { return 0; }
  return (events / total) * 100;
}
